import React, { useCallback, useEffect } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import { Modal, Tappable } from '@vectord/components';
import classNames from 'classnames';
import cogoToast from 'cogo-toast';

import {
  creditCountSelector,
  isConfirmModalOpenSelector,
  learnerEmailSelector,
  otherReasonInputSelector,
  reasonSelector,
  setConfirmModalOpen,
  setCreditCount,
  setLearnerEmail,
  setReason,
  transactionTypeSelector,
} from 'features/creditManagement';
import { useAddCreditPointsMutation } from 'services/creditManagementService';
import analytics from 'utils/analytics';
import styles from './ManageCreditConfirmModal.module.scss';

function ManageCreditConfirmModal() {
  const dispatch = useDispatch();

  const isConfirmModalOpen = useSelector(isConfirmModalOpenSelector);
  const creditCount = useSelector(creditCountSelector);
  const email = useSelector(learnerEmailSelector);
  const reason = useSelector(reasonSelector);
  const otherReasonInput = useSelector(otherReasonInputSelector);
  const transactionType = useSelector(transactionTypeSelector);

  const [createAddCreditRequest, { isSuccess, isLoading, error }] =
    useAddCreditPointsMutation();

  const handleOnClose = useCallback(() => {
    dispatch(setConfirmModalOpen(false));
  }, [dispatch]);

  const handleConfirmAction = useCallback(() => {
    createAddCreditRequest({
      email,
      note: reason?.tag,
      value: creditCount,
      transaction_type: transactionType,
      note_reason: otherReasonInput,
    });
    analytics?.click({
      click_type: 'Credit Management Confirm Clicked',
      custom: {
        email,
        note: reason?.tag,
        value: creditCount,
        transaction_type: transactionType,
        note_reason: otherReasonInput,
      },
    });
  }, [
    createAddCreditRequest,
    creditCount,
    email,
    otherReasonInput,
    reason,
    transactionType,
  ]);

  useEffect(() => {
    if (isSuccess) {
      cogoToast.success(`Credits Updated!`);

      batch(() => {
        dispatch(setCreditCount(0));
        dispatch(setLearnerEmail(''));
        dispatch(setReason(null));
        dispatch(setConfirmModalOpen(false));
      });
    }
  }, [dispatch, isSuccess]);

  useEffect(() => {
    if (error) {
      const message =
        error?.data?.message || 'Something went wrong! Try again later';
      cogoToast.error(message);
    }
  }, [error]);

  return (
    <Modal
      isOpen={isConfirmModalOpen}
      unMountOnClose
      onClose={handleOnClose}
      title={`${
        transactionType === 'ADD' ? 'Add' : 'Deduct'
      } Credit Confirmation:`}
      className={styles.root}
      canClose={!isLoading}
    >
      <div className={styles.container}>
        <div className={classNames(styles.header, 'm-b-10')}>
          Learner Email:
          <span className={classNames(styles.content, 'm-t-xxs', 'm-l-5')}>
            {email}
          </span>
        </div>
        <div className={classNames(styles.header, 'm-b-10')}>
          Credits:
          <span className={classNames(styles.content, 'm-t-xxs', 'm-l-5')}>
            {transactionType === 'ADD' ? '+' : '-'}
            {creditCount}
          </span>
        </div>
        <div className={styles.header}>Reason:</div>
        <div className={classNames(styles.content, 'm-t-xxs')}>
          {otherReasonInput !== '' ? otherReasonInput : reason?.label}
        </div>
      </div>
      <Tappable
        className={classNames('btn btn-primary m-t-10', styles.root)}
        onClick={handleConfirmAction}
        disabled={isLoading}
      >
        Confirm
      </Tappable>
    </Modal>
  );
}

export default ManageCreditConfirmModal;
